import { useEffect } from 'react'
import { Loader2 } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { usePlanOptions } from '@/entities/billing/model/queries'
import type { BillingInterval, PlanOption } from '@/entities/billing/model/types'

/**
 * The plan a new organization's trial runs inside, picked while the organization is created.
 *
 * The trial lives inside the chosen plan's caps, so what is shown here has to be the limits that will
 * actually bite — not a sales pitch. Each row therefore carries the plan's real site and user caps next
 * to its price, and the fine print says plainly that no card is taken and that users are billed on top.
 *
 * Five priced rows with no framing read as a checkout, which is a reason to abandon a signup that costs
 * nothing. The note under the label is the live pricing page's own sentence, so the question stays
 * "which plan am I trialling" rather than "what am I buying today".
 */

/**
 * Copy lifted verbatim from the published pricing page, so the product cannot say something the site
 * does not. `TRIAL_NOTE` is `PricingTable.astro`'s own note under "Start free trial"; "the same system
 * a paying company uses" is `pricing.astro`'s trial section; `ENTERPRISE` mirrors `product.ts`.
 *
 * These are copies, not a shared source: Enterprise is `is_public = false` and a marketing sentence
 * does not belong in a billing table. So they stay copies, and a CHECK compares them —
 * `Tools/PricingDriftCheck/check.mjs` in NetZForceGMS-Backend, run from this repository's CI as
 * `.github/workflows/pricing-drift.yml`. It parses `ENTERPRISE` below and the site's own
 * `product.ts` and fails naming the field and both values, so changing one side without the other
 * no longer drifts quietly. `TRIAL_NOTE` is deliberately outside it: it is prose, and a check that
 * trips when a sentence is polished is a check that gets muted.
 */
const TRIAL_NOTE = 'Two weeks free, no card. Every plan runs the full system.'

/**
 * Shown as a contact route, never as something to pick. There is no price to write on a subscription
 * row and `enterprise` is `is_public = false` precisely because it is negotiated, so the API would
 * refuse it anyway — but the pricing page publishes the tile, and what the site publishes has to hold
 * true here. A firm with 400 guards must not reach Pro's 250 and find nothing beyond it.
 */
const ENTERPRISE = {
  name: 'Enterprise',
  note: 'tailored pricing & terms',
  users: 'Unlimited',
  sites: '50+',
  email: 'hello@nezunatechnologies.com',
} as const

/**
 * Pre-selected when the catalogue loads. Deliberately not the cheapest: Solo covers 10 users and 2
 * sites, which a firm trialling with its real roster can pass inside a fortnight, and a default that
 * quietly undersizes everybody is how the trial stops resembling the product. Starter is roomy enough
 * for a typical new firm while still being a real cap they can feel, and the caps are printed on every
 * row so moving up or down is one click.
 */
const PREFERRED_DEFAULT_PLAN = 'starter'

const INTERVALS: { value: BillingInterval; label: string }[] = [
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Annual', label: 'Annual' },
]

/** Cents to a plain price. Whole dollars stay whole: "$67", not "$67.00". */
function formatPrice(cents: number): string {
  const amount = cents / 100
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'CAD',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

function priceFor(plan: PlanOption, interval: BillingInterval): number | null {
  return interval === 'Annual' ? plan.annualPlatformCents : plan.monthlyPlatformCents
}

/**
 * The per-user rate for the chosen period. READ for the interval, never derived from the other one:
 * the annual discount applies to the plan fee only, so the annual seat rate is its own published
 * figure.
 */
function seatRateFor(plan: PlanOption, interval: BillingInterval): number | null {
  return interval === 'Annual' ? plan.annualSeatCents : plan.monthlySeatCents
}

/**
 * The fine print under the list.
 *
 * It used to say "active users are billed per month on top of the plan price", which described the
 * old model: every person signed in was a line on the bill. They are not. The plan INCLUDES its
 * users, and the rate applies only to the ones bought beyond that — so the sentence has to say
 * "extra", or it tells a firm of twenty that its Starter plan costs the fee plus twenty users.
 *
 * The rate comes from the plan the person has actually selected, so the sentence tracks the Monthly /
 * Annual toggle instead of quoting a number the rows above contradict. Every published plan shares
 * one rate today, but reading it off the chosen plan rather than off the first row means a plan that
 * one day does not will still be described correctly. Falls back to the plainer wording if a plan
 * somehow has no rate, rather than printing a gap.
 */
function seatNote(plan: PlanOption | undefined, interval: BillingInterval): string {
  const base = "Your trial runs inside this plan's limits, on the same system a paying company uses."
  const rate = plan ? seatRateFor(plan, interval) : null
  if (rate === null) {
    return `${base} The users your plan includes cost nothing extra; beyond that you can buy more per month.`
  }
  return `${base} The users your plan includes cost nothing extra. Beyond that, each one is ${formatPrice(rate)} a month.`
}

/**
 * "25 users included · 5 sites".
 *
 * "Up to 25 users" read as a ceiling you may not pass, which is how the old model worked. It is an
 * allowance now: pass it and you buy more, which is cheaper than moving plan, because plans are
 * priced on sites. A null cap is unlimited, which is never 0 and never a selectable plan.
 */
function capsFor(plan: PlanOption): string {
  const users = plan.maxUsers === null ? 'Unlimited users' : `${plan.maxUsers} users included`
  const sites = plan.maxSites === null ? 'unlimited sites' : `${plan.maxSites} sites`
  return `${users} · ${sites}`
}

type PlanChoiceFieldProps = {
  /** The chosen plan code, or '' before the catalogue has loaded or if nothing is chosen. */
  value: string
  onChange: (planCode: string) => void
  interval: BillingInterval
  onIntervalChange: (interval: BillingInterval) => void
  /** Shown under the list; the form's own validation message for an unchosen plan. */
  error?: string
  disabled?: boolean
}

export function PlanChoiceField({
  value,
  onChange,
  interval,
  onIntervalChange,
  error,
  disabled = false,
}: PlanChoiceFieldProps) {
  const { data: plans, isPending, isError } = usePlanOptions()

  // Pre-select as soon as there is a catalogue to pre-select from. Guarded on an empty value, so it
  // runs once and can never overwrite what someone has clicked.
  useEffect(() => {
    if (value !== '' || !plans || plans.length === 0) {
      return
    }
    const preferred = plans.find((plan) => plan.code === PREFERRED_DEFAULT_PLAN) ?? plans[0]
    onChange(preferred.code)
  }, [plans, value, onChange])

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-ink-2">Your plan</span>
        <div className="flex items-center gap-1" role="group" aria-label="Billing period">
          {INTERVALS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onIntervalChange(option.value)}
              aria-pressed={interval === option.value}
              disabled={disabled}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                interval === option.value
                  ? 'bg-indigo-600 text-white'
                  : 'text-ink-3 hover:bg-surface-mute hover:text-ink',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-1 text-xs text-ink-3">{TRIAL_NOTE}</p>

      {isPending && (
        <div className="mt-2 flex h-20 items-center justify-center rounded-xl border border-line bg-surface-mute/50 text-sm text-ink-3">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading plans
        </div>
      )}

      {isError && (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          We could not load the plans just now. Please reload the page and try again.
        </div>
      )}

      {plans && plans.length > 0 && (
        <div className="mt-2 space-y-2">
          {plans.map((plan) => {
            const selected = plan.code === value
            const price = priceFor(plan, interval)
            return (
              <label
                key={plan.code}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-3 transition-colors',
                  // A translucent indigo over whatever surface is underneath, rather than a fixed
                  // light tint: the same rule then reads correctly in both themes, where
                  // `bg-indigo-50` would put light-mode text on a light-mode panel in dark mode.
                  selected
                    ? 'border-indigo-600 bg-indigo-600/10 ring-1 ring-indigo-600'
                    : 'border-line bg-surface-mute/50 hover:bg-surface-mute',
                  disabled && 'cursor-not-allowed opacity-60',
                )}
              >
                <span className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="planCode"
                    value={plan.code}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => onChange(plan.code)}
                    className="mt-0.5 h-4 w-4 accent-indigo-600"
                  />
                  <span className="flex flex-col">
                    <span className="text-[15px] font-semibold text-ink">{plan.name}</span>
                    <span className="text-xs text-ink-3">{capsFor(plan)}</span>
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  {price === null ? (
                    <span className="text-sm text-ink-3">Custom</span>
                  ) : (
                    <>
                      <span className="text-[15px] font-semibold text-ink">
                        {formatPrice(price)}
                      </span>
                      <span className="text-xs text-ink-3">/mo</span>
                      {interval === 'Annual' && (
                        <span className="block text-xs text-ink-3">billed annually</span>
                      )}
                    </>
                  )}
                </span>
              </label>
            )
          })}

          {/*
            A tile, not an option. Deliberately a div with no radio and no name="planCode", so it is
            outside the group the form reads and cannot become a chosen plan however it is clicked or
            tabbed through — the customer contacts us and still finishes signup on a published plan.
            The leading spacer keeps its text on the same left edge as the rows that do have a radio.
          */}
          <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-line bg-surface-mute/30 p-3">
            <span className="flex items-start gap-3">
              <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="flex flex-col">
                <span className="text-[15px] font-semibold text-ink">{ENTERPRISE.name}</span>
                <span className="text-xs text-ink-3">
                  {ENTERPRISE.users} users · {ENTERPRISE.sites} sites
                </span>
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className="block text-sm font-semibold text-ink">Custom</span>
              <a
                href={`mailto:${ENTERPRISE.email}`}
                className="text-xs font-semibold text-indigo-600 hover:underline"
              >
                Contact sales
              </a>
            </span>
          </div>
          <p className="text-xs text-ink-3">
            Enterprise is {ENTERPRISE.note} — get in touch, and start your trial on a plan above in
            the meantime.
          </p>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}

      <p className="mt-2 text-xs text-ink-3">
        {seatNote(
          plans?.find((plan) => plan.code === value),
          interval,
        )}
      </p>
    </div>
  )
}
